import { useMemo } from "react";
import { useQuery } from "urql";
import { graphql } from "@acto/gql";
import { TodoType } from "@acto/gql/graphql";
import { Todo } from "@acto/taskpane/core/domain";
import { useFlagWithContext } from "@acto/web-common/feature-flags";

const ClientTodosQuery = graphql(`
  query GetClientTodos($clientId: ID!, $todoTypes: [TodoType!]) {
    clients(first: 1, filter: { id: $clientId }) {
      edges {
        node {
          id
          name
          externalReference
          todos(todoTypes: $todoTypes) {
            edges {
              node {
                id
                title
                description
                dueDate
                processedStatus
                processedReason
                isWorkflow
                isNoteBased
                assignedUser {
                  id
                  __typename
                }
                client {
                  id
                  legacyId
                  name
                  externalReference
                }
              }
            }
          }
        }
      }
    }
  }
`);

interface UseCustomerTodosByClientIdResult {
  data: Todo[] | undefined;
  clientName: string | undefined;
  clientExternalReference: string | null;
  isPending: boolean;
  isFetching: boolean;
  error: Error | undefined;
}

export function useCustomerTodosByClientId(clientId: string): UseCustomerTodosByClientIdResult {
  const isNoteBasedTodosEnabled = useFlagWithContext("note-based-todos", false);

  const todoTypes = useMemo(() => {
    const types: TodoType[] = [TodoType.Workflow];
    if (isNoteBasedTodosEnabled) {
      types.push(TodoType.NoteBased);
    }
    return types;
  }, [isNoteBasedTodosEnabled]);

  const [result] = useQuery({
    query: ClientTodosQuery,
    variables: { clientId, todoTypes },
    pause: !clientId,
  });

  const clientNode = result.data?.clients?.edges?.[0]?.node;

  const todos = useMemo(() => {
    const todoEdges = clientNode?.todos?.edges;
    if (!todoEdges) return undefined;
    return todoEdges.map((edge) => Todo.fromGraphql(edge.node));
  }, [clientNode?.todos?.edges]);

  const clientName = clientNode?.name || clientNode?.externalReference || undefined;
  const clientExternalReference = clientNode?.externalReference ?? null;

  return {
    data: todos,
    clientName,
    clientExternalReference,
    isPending: result.fetching && !result.data,
    isFetching: result.fetching,
    error: result.error,
  };
}
